import "./flows.css";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import React, {useEffect, useState} from "react";
import {MessageTemplateProps} from "../../types/MessageTemplateProps";
import Page from "../../components/page/Page";
import Skeleton from "../../components/skeleton/Skeleton";
import EmptyState from "../../components/emptyState/EmptyState";
import {Button} from "antd";
import WhatsappIntegrationButton from "../../components/whatsappIntegrationButton/WhatsappIntegrationButton";
import CouponsProducts from "../couponsProducts/CouponsProducts";
import {FlowProps} from "../../types/FlowProps";
import {FlowTypeProps} from "../../types/FlowTypeProps";
import FlowRecord from "./FlowRecord";
import {useLocation, useNavigate} from "react-router-dom";
import NewFlowModal from "../../components/modals/flowModals/NewFlowModal";
import {flowStats} from "../../api/flowStats";
import useFlag from "../../hooks/useFlag";

const Flows = () => {
    const { data: { templates, flows, flowTypes, flowTriggers, waba }  } = useAppContext();
    const { selectedDomain, onboardingStatus: { isFullyOnboarded, isOnboardingStatusLoaded, hasContactPhone, hasContacts, hasContactEmail, hasPhone, hasPricingPlan, hasWaba } } = useUserContext();
    const [isNewNotificationModalOpen, setIsNewNotificationModalOpen] = useState(false);
    const [isNewFlowModalOpen, setIsNewFlowModalOpen] = useState(false);
    const [isPresetsModalOpen, setIsPresetsModalOpen] = useState(false);
    const [editedNotification, setEditedNotification] = useState<MessageTemplateProps | null>(null);
    const [isCouponManagementOn, setIsCouponManagementOn] = useState(false);
    const [selectedFlowTriggerId, setSelectedFlowTriggerId] = useState(-1);
    const [stats, setStats] = useState<Map<number, any>>(new Map());
    const { isFlagOn } = useFlag();
    const isV2 = isFlagOn('v2');
    const navigate = useNavigate();
    const location = useLocation();

    const closeEditNotificationModal = () => {
        setEditedNotification(null);
    }

    const closeFlowModal = () => {
        setIsNewFlowModalOpen(false);
    }

    const onNotificationClick = (template: MessageTemplateProps | null) =>  () => {
        template && setEditedNotification(template);
    }

    const toggleCouponsManagementPanel = () => {
        setIsCouponManagementOn(!isCouponManagementOn);
    }

    const togglePresetsModal = (openCoupons: boolean = false) => {
        setIsPresetsModalOpen(false);
        if(openCoupons) {
            setIsCouponManagementOn(true);
        }
    }

    const loadStats = async () => {
        if(!selectedDomain) return;
        const response = await flowStats(selectedDomain);
        const newMap = new Map();
        response.data.map((flowStats: any) => {
            newMap.set(flowStats.id, flowStats);
        })
        setStats(newMap);
    }

    useEffect(() => {
        loadStats();
    }, [selectedDomain]);

    useEffect(() => {
        if (location.state?.openCouponPanel) {
            toggleCouponsManagementPanel();
            navigate(".", { replace: true, state: {} });
        }
    }, [location.state, navigate]);

    const isEnabled = isFullyOnboarded;
    const isEmpty = !isEnabled || !flows.data.length;
    const flowsData: FlowProps[] = flows.data;
    const selectedFlowTrigger = flowTriggers.data.find((flowTrigger: FlowTypeProps) => flowTrigger.id === selectedFlowTriggerId);

    return (
        <Page
            className="flows-container"
            title="Automations"
            subtitle="Automations allow you to set up workflows that trigger specific actions based on customer behaviors, such as abandoned cart recovery, post-purchase follow-ups, and more. These flows are designed to run automatically, saving you time and ensuring timely communication with your customers."
            primaryAction={{
                label: 'Add Automation',
                action: () => setIsNewFlowModalOpen(true),
                isDisabled: !isEnabled,
            }}
            secondaryActions={[{
                label: 'Coupons',
                action: toggleCouponsManagementPanel,
                isDisabled: !isEnabled
            }]}
        >
            {!isOnboardingStatusLoaded || flows.isLoading || templates.isLoading || waba.isLoading ? <Skeleton/> :
                isEmpty ? <EmptyState
                        title="You don't have any automations yet"
                        subTitle={isEnabled ? "Create new automations and templates for different scenarios, test them and use them to communicate with your clients" : "Before creating new automations and templates, you must create and connect your Whatsapp Business Account. Click the button below, follow Meta's instructions and in 5 minutes you'll start creating templates."}
                        actions={isEnabled ? <Button type="primary" onClick={() => setIsNewFlowModalOpen(true)} size="large" >Add Automation</Button> : isV2 ? <Button type="primary" onClick={() => navigate('/vivo')} size="large" >Complete onboarding with Vivo</Button> : <WhatsappIntegrationButton /> }
                        className={isEnabled ? "flows-empty-state" : "flows-empty-state disabled"}
                    />
                    :
                    <>
                        <div className="filters">
                            <div className="flow-type-filter">
                                <div className="label">Automation Trigger: </div>
                                <div className="option-buttons">
                                    <div className={`flow-trigger-option ${selectedFlowTriggerId === -1 ? 'selected' : ''}`} onClick={() => setSelectedFlowTriggerId(-1)}>All Triggers</div>
                                    {flowTriggers.data.map((flowTrigger: FlowTypeProps) => <div key={`flow-trigger-${flowTrigger.id}`} className={`flow-trigger-option ${selectedFlowTriggerId === flowTrigger.id ? 'selected' : ''}`} onClick={() => setSelectedFlowTriggerId(flowTrigger.id)}>{flowTrigger.title}</div>)}
                                </div>
                            </div>
                            <div className="flow-search">
                            </div>
                        </div>
                        <div className="flows-list-container">
                            {flowsData.filter((flow: FlowProps) => selectedFlowTriggerId === -1 || flow.flowTriggerId === selectedFlowTriggerId).map((flow: FlowProps, index: number) =>
                                <FlowRecord
                                    key={`flow-${flow.id}`}
                                    index={index}
                                    flowTypeId={flow.flowTypeId}
                                    flow={flow}
                                    onNotificationClick={onNotificationClick}
                                    setIsNewNotificationModalOpen={setIsNewNotificationModalOpen}
                                    setIsPresetsModalOpen={setIsPresetsModalOpen}
                                    stats={stats.get(flow.id)}
                                />
                            )}
                        </div>
                    </>
            }
            <CouponsProducts isOpen={isCouponManagementOn} onClose={toggleCouponsManagementPanel} />
            <NewFlowModal isOpen={isNewFlowModalOpen} onClose={closeFlowModal} />
        </Page>
    );
}

export default Flows;